export interface ClientProject {
  id: string;
  name: string;
  tagline: string;
  description: string;
  category: 'ecommerce' | 'corporate' | 'webapp' | 'mobile';
  imageLaptop?: string;
  imagePhone?: string;
  accentColor: string;
  services: string[];
  techStack: string[];
  results?: string[];
  testimonial?: {
    quote: string;
    author: string;
    role: string;
  };
  links: {
    live?: string;
    caseStudy?: string;
  };
  featured: boolean;
}

export const clients: ClientProject[] = [
  {
    id: 'irt-reels',
    name: 'IRT Reels',
    tagline: 'Premium Fishing Reels E-Commerce',
    description: 'Complete e-commerce solution for a premium fishing reel manufacturer. Built a custom Shopify storefront with advanced product filtering, inventory management, and seamless checkout experience. Integrated with their fulfillment and CRM systems.',
    category: 'ecommerce',
    imageLaptop: '/assets/irtLaptop.png',
    imagePhone: '/assets/irtPhone.png',
    accentColor: '#1a5f7a',
    services: [
      'E-Commerce Development',
      'Shopify Customization',
      'Inventory Integration',
      'Payment Processing',
      'SEO Optimization',
    ],
    techStack: ['Shopify', 'Liquid', 'JavaScript', 'CSS'],
    results: [
      'Increased online sales by 150%',
      'Reduced cart abandonment by 35%',
      'Improved page load time by 60%',
    ],
    links: {
      live: 'https://irtreels.com',
    },
    featured: true,
  },
  {
    id: 'fiberon',
    name: 'Fiberon Decking',
    tagline: 'Corporate Website Rebuild',
    description: 'Modern corporate website for a leading composite decking manufacturer. Implemented a product showcase with 3D visualization, dealer locator, and comprehensive resource library. Focused on performance and mobile responsiveness.',
    category: 'corporate',
    imageLaptop: '/assets/fiberonLaptop.png',
    accentColor: '#8b4513',
    services: [
      'Web Development',
      'UI/UX Design',
      'CMS Integration',
      'Performance Optimization',
      'Analytics Setup',
    ],
    techStack: ['React', 'TypeScript', 'Node.js', 'PostgreSQL'],
    results: [
      'Page views increased 200%',
      'Mobile traffic up 180%',
      'Lead generation improved 75%',
    ],
    links: {
      live: 'https://fiberondecking.com',
    },
    featured: true,
  },
  {
    id: '501-pub',
    name: '501 Pub',
    tagline: 'Local Bar & Restaurant Website',
    description: 'Modern website for a local pub featuring menu displays, event calendars, and online reservations. Built with a focus on mobile-first design and fast load times to capture customers on the go.',
    category: 'corporate',
    imageLaptop: '/assets/501Laptop.png',
    accentColor: '#2d5016',
    services: [
      'Web Development',
      'UI/UX Design',
      'Menu Management',
      'Mobile Optimization',
    ],
    techStack: ['React', 'TypeScript', 'Firebase'],
    links: {
      live: 'https://501-pub.com',
    },
    featured: true,
  },
  {
    id: 'solar-innovations',
    name: 'Solar Innovations',
    tagline: 'Architectural Glazing Systems',
    description: 'Web presence for a 25+ year industry leader in custom greenhouses, sunrooms, and conservatories. WordPress build on WPengine surfacing a deep product catalog with technical specs, dealer resources, and project galleries — built to support a serious B2B operation that architects and contractors depend on.',
    category: 'corporate',
    imageLaptop: '/assets/solarinnovationsLaptop.png',
    accentColor: '#1e5631',
    services: [
      'Web Development',
      'WordPress Customization',
      'Product Catalog',
      'Performance Optimization',
      'Managed Hosting',
    ],
    techStack: ['WordPress', 'PHP', 'WPengine', 'JavaScript'],
    links: {
      live: 'https://solarinnovations.com',
    },
    featured: true,
  },
  {
    id: 'wampum-hardware',
    name: 'Wampum Hardware',
    tagline: '120-Year Industrial Brand, Modern Web',
    description: 'Web presence for Wampum Hardware Co — a premium explosives supplier serving drilling and blasting operations since 1904. Custom React build that brings a 120-year-old industrial brand to modern web standards: multi-location finder, careers portal, services overview, and safety resources for an industry where credibility and reliability are everything.',
    category: 'corporate',
    imageLaptop: '/assets/wampumLaptop.png',
    accentColor: '#c41e3a',
    services: [
      'Custom Web Development',
      'Brand Modernization',
      'Multi-Location Architecture',
      'Careers Portal',
      'Mobile Responsive Design',
    ],
    techStack: ['React', 'TypeScript', 'Vite', 'CSS'],
    links: {
      live: 'https://wampumhardware.com',
    },
    featured: true,
  },
  {
    id: 'lebanon-expo',
    name: 'Lebanon Expo',
    tagline: 'Community Expo Event Platform',
    description: 'Event website for a community expo featuring vendor registration, event schedules, and attendee information. Built to handle high traffic during event announcements and registration periods.',
    category: 'webapp',
    imageLaptop: '/assets/lebanonLaptop.png',
    accentColor: '#b8860b',
    services: [
      'Web Development',
      'Event Management',
      'Registration System',
      'Responsive Design',
    ],
    techStack: ['React', 'TypeScript', 'Firebase'],
    links: {
      live: 'https://lebexpo.org',
    },
    featured: true,
  },
];

export const featuredClients = clients.filter(client => client.featured);

export const clientCategories = [
  { id: 'all', label: 'All Projects' },
  { id: 'ecommerce', label: 'E-Commerce' },
  { id: 'corporate', label: 'Corporate' },
  { id: 'webapp', label: 'Web Apps' },
  { id: 'mobile', label: 'Mobile' },
] as const;

export function getClientById(id: string): ClientProject | undefined {
  return clients.find(client => client.id === id);
}

export function getClientsByCategory(category: string): ClientProject[] {
  if (category === 'all') return clients;
  return clients.filter(client => client.category === category);
}
